import { useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import bookingsApi from "@/api/bookingsApi";
import extrasApi from "@/api/extrasApi";
import propertiesApi from "@/api/propertiesApi";
import PriceBreakdown from "@/components/PriceBreakdown/PriceBreakdown";
import ApiError from "@/model/ApiError";
import ExtraResponse from "@/model/ExtraResponse";
import PropertyResponse from "@/model/PropertyResponse";
import isoDate from "@/utils/isoDate";
import {
  computeExtraTotal,
  computeNights,
  computeStayQuote,
  formatMoney,
} from "@/utils/pricing";
import guestsUtils from "@/utils/guests";
import "@/pages/BookingCheckoutPage/BookingCheckoutPage.scss";

function BookingCheckoutPage() {
  const { t, i18n } = useTranslation();
  const { session } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const checkIn = searchParams.get("checkIn") ?? "";
  const checkOut = searchParams.get("checkOut") ?? "";
  const guests =
    guestsUtils.fromSearchParams(searchParams) ?? guestsUtils.ONE_ADULT;
  const guestCount = guestsUtils.countedGuests(guests);
  const nights = computeNights(checkIn, checkOut);

  const [property, setProperty] = useState<PropertyResponse | null>(null);
  const [extras, setExtras] = useState<ExtraResponse[]>([]);
  const [selectedExtraIds, setSelectedExtraIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!id) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);

    Promise.all([propertiesApi.getPropertyById(id), extrasApi.listExtras()])
      .then(([loadedProperty, extraPage]) => {
        if (cancelled) {
          return;
        }
        setProperty(loadedProperty);
        // The backend refuses extras priced in another currency than the property.
        setExtras(
          (extraPage.content ?? []).filter(
            (extra) =>
              extra.id && extra.priceCurrency === loadedProperty.priceCurrency
          )
        );
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!id || !session) {
    return null;
  }

  if (nights <= 0) {
    return (
      <div className="booking-checkout-page">
        <p role="alert" className="booking-checkout-page__message">
          {t("checkout.missingDates")}
        </p>
        <Link to={`/properties/${id}`}>{t("checkout.backToProperty")}</Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="booking-checkout-page">
        <p>{t("checkout.loading")}</p>
      </div>
    );
  }

  if (loadError || !property) {
    return (
      <div className="booking-checkout-page">
        <p role="alert" className="booking-checkout-page__message">
          {t("checkout.loadError")}
        </p>
        <Link to="/">{t("propertyDetails.back")}</Link>
      </div>
    );
  }

  const selectedExtras = extras.filter(
    (extra) => extra.id && selectedExtraIds.includes(extra.id)
  );
  const quote = computeStayQuote(property, nights, guestCount, selectedExtras);

  const toggleExtra = (extraId: string) =>
    setSelectedExtraIds((current) =>
      current.includes(extraId)
        ? current.filter((idItem) => idItem !== extraId)
        : [...current, extraId]
    );

  const handleConfirm = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      // Holds the nights and creates the Stripe payment; paying happens on the next page.
      const checkout = await bookingsApi.createBooking(session.accessToken, {
        propertyId: id,
        checkIn,
        checkOut,
        guests,
        extraIds: selectedExtraIds,
      });
      navigate(`/trips/${checkout.booking?.id}/pay`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(t("checkout.conflictError"));
      } else if (err instanceof ApiError && err.status === 400) {
        setError(err.message || t("checkout.validationError"));
      } else if (err instanceof ApiError && err.status === 502) {
        setError(t("checkout.paymentUnavailable"));
      } else {
        setError(t("checkout.genericError"));
      }
      setIsSubmitting(false);
    }
  };

  const backSearch = searchParams.toString();

  return (
    <div className="booking-checkout-page">
      <Link
        to={`/properties/${id}?${backSearch}`}
        className="booking-checkout-page__back"
      >
        {t("checkout.backToProperty")}
      </Link>

      <h1>
        {property.bookingMode === "REQUEST"
          ? t("checkout.requestTitle")
          : t("checkout.title")}
      </h1>

      <div className="booking-checkout-page__layout">
        <div className="booking-checkout-page__main">
          <section>
            <h2>{t("checkout.tripTitle")}</h2>
            <dl className="booking-checkout-page__trip">
              <dt>{t("checkout.dates")}</dt>
              <dd>
                {isoDate.formatIsoDate(checkIn, i18n.language)} →{" "}
                {isoDate.formatIsoDate(checkOut, i18n.language)}
              </dd>
              <dt>{t("checkout.guests")}</dt>
              <dd>{guestsUtils.describe(t, guests)}</dd>
            </dl>
          </section>

          <section>
            <h2>{t("checkout.extrasTitle")}</h2>
            {extras.length === 0 ? (
              <p className="booking-checkout-page__muted">
                {t("checkout.noExtras")}
              </p>
            ) : (
              <ul className="booking-checkout-page__extras">
                {extras.map((extra) => (
                  <li key={extra.id}>
                    <label className="booking-checkout-page__extra">
                      <input
                        type="checkbox"
                        checked={selectedExtraIds.includes(extra.id as string)}
                        onChange={() => toggleExtra(extra.id as string)}
                      />
                      <span className="booking-checkout-page__extra-text">
                        <strong>{extra.name}</strong>
                        {extra.description && <span>{extra.description}</span>}
                      </span>
                      <span className="booking-checkout-page__extra-price">
                        {formatMoney(
                          extra.priceAmount,
                          extra.priceCurrency,
                          i18n.language
                        )}{" "}
                        <small>
                          {t(`extras.pricingUnit.${extra.pricingUnit}`)}
                        </small>
                        <br />
                        <small>
                          {t("checkout.extraTotal", {
                            total: formatMoney(
                              computeExtraTotal(extra, nights, guestCount),
                              extra.priceCurrency,
                              i18n.language
                            ),
                          })}
                        </small>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {property.houseRules && (
            <section>
              <h2>{t("propertyDetails.rulesTitle")}</h2>
              <p className="booking-checkout-page__rules">
                {property.houseRules}
              </p>
            </section>
          )}
        </div>

        <aside className="booking-checkout-page__sidebar">
          <div className="booking-checkout-page__property">
            {property.imageUrls?.[0] && (
              <img src={property.imageUrls[0]} alt="" />
            )}
            <div>
              <strong>{property.title}</strong>
              <span>
                {[property.address?.city, property.address?.country]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            </div>
          </div>

          <PriceBreakdown quote={quote} />

          {error && (
            <p className="booking-checkout-page__error" role="alert">
              {error}
            </p>
          )}

          <button type="button" onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting
              ? t("checkout.confirming")
              : t("checkout.confirmButton")}
          </button>
          <p className="booking-checkout-page__muted">
            {property.bookingMode === "REQUEST"
              ? t("checkout.requestNote")
              : t("checkout.instantBookingNote")}
          </p>
        </aside>
      </div>
    </div>
  );
}

export default BookingCheckoutPage;
